/**
 * What a Season 3 squad entry must be — the Society Team League's rules for a
 * team owner proposing a side.
 *
 * SHARED BY THE FORM AND THE ENDPOINT, so the owner is told exactly what the
 * server will refuse, before he sends it, in the same words.
 *
 * The organiser's terms (27 October 2026): each player with a full name, a
 * Playtomic rating and an actual player rating.
 *
 * TEN PLAYERS AND 24 STARS since 1 November 2026, when the league moved
 * Season 3 to four games a night: eight play and two sit out. The cap was set
 * at 25 (2.5 a player, Season 1's average) and taken down to 24 the same day:
 * stars round DOWN, so a "2.5-star" player is anyone rated 2.50-2.99 and 25
 * stars let a side field ten 2.9s. 24 keeps the real average nearer 2.5. It
 * was nine players and 23 stars when entries opened, so an entry sent before
 * the change may hold nine; it stays on record and is shown as needing its
 * tenth (Season3Card, Admin).
 *
 * THE LIMIT IS IN STARS, not rating points (the league's decision, 27 October
 * 2026, replacing a first draft of 24 rating points).
 * Each player's actual rating is banded into half-stars (starsFor), so 2.31
 * and 2.36 are the same 2 stars and nobody argues over the second decimal.
 * Playtomic is collected for the committee's checking, never counted.
 */

export const SQUAD_SIZE = 10;
/** Stars a squad may total. */
export const SQUAD_LIMIT = 24;
export const STAR_MIN = 1.5;
export const STAR_MAX = 3.5;

/**
 * A rating in stars: rounded DOWN to the half, at least 1.5, at most 3.5.
 *
 *   below 2.00 → 1.5   2.00–2.49 → 2   2.50–2.99 → 2.5   3.00–3.49 → 3   3.50 up → 3.5
 *
 * Worked in hundredths, because 2.5 * 2 in floating point is not always 5.
 */
export function starsFor(rating: number): number {
  if (!Number.isFinite(rating)) return Number.NaN;
  const halves = Math.floor(Math.round(rating * 100) / 50);
  return Math.min(STAR_MAX, Math.max(STAR_MIN, halves / 2));
}

/** "· 2.5★" beside a rating box, so a player's stars show as the rating is typed. */
export function typedStars(typed: string): string {
  const r = readRating(typed);
  return r.kind === 'ok' ? ` · ${starText(starsFor(r.value))}\u2605` : '';
}

/** The bands, as both forms print them. */
export const STAR_BANDS = [
  ['Under 2.00', 1.5],
  ['2.00 – 2.49', 2],
  ['2.50 – 2.99', 2.5],
  ['3.00 – 3.49', 3],
  ['3.50 and up', 3.5],
] as const;

/** "2.5", "3", "21.5" — stars as people say them. */
export const starText = (stars: number) => (Number.isInteger(stars) ? String(stars) : stars.toFixed(1));

/** A rating as typed: "2,75" from a South African keypad, or "2.75". */
export type RatingReading =
  | { kind: 'empty' }
  | { kind: 'ok'; value: number }
  | { kind: 'error'; message: string };

/**
 * Reads a rating, comma or full stop, up to two decimals, 0 to 7.
 *
 * Its own reader rather than the registration form's readLevel, which carries
 * Premier Pearls' 1.3 entry floor — a rule of theirs, not the Society's. And,
 * like that one, it never guesses: 275 is pointed out, not read as 2.75.
 */
export function readRating(typed: string): RatingReading {
  const text = typed.trim();
  if (text === '') return { kind: 'empty' };
  const normal = text.replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(normal)) {
    return { kind: 'error', message: 'Type it like 2,75 or 2.75.' };
  }
  const value = Number(normal);
  if (!normal.includes('.') && value > 7) {
    return { kind: 'error', message: `That reads as ${value} — did you mean a comma in it, like 2,75?` };
  }
  if (value > 7) return { kind: 'error', message: 'Ratings run from 0 to 7.' };
  return { kind: 'ok', value };
}

export interface EnteredPlayer {
  fullName: string;
  /**
   * Null for a RETURNING player: his stars come from his TrueRank, so his
   * Playtomic would be checked against nothing and is not asked for. Every
   * player whose rating is self-declared must give one.
   */
  playtomic: number | null;
  rating: number;
}

export interface SquadCheck {
  /** Whether it can be SENT — which an over-limit squad can; see `over`. */
  ok: boolean;
  /** The nine actual ratings added up, to two decimals. */
  total: number;
  /** The nine players' stars added up. This is what the limit is read on. */
  stars: number;
  /** Every reason the squad cannot be sent, in the order to fix them. */
  problems: string[];
  /**
   * Over the star limit. NOT a reason to refuse the squad: the league keeps
   * every entry as a lead (27 October 2026), and an over-limit squad is sent,
   * marked, and not accepted until it is brought down — by the owner
   * resending, or the captain changing the answer — before entries close.
   */
  over: boolean;
}

/** Adds money-style, so 2.10 + 2.20 is 4.30 and not 4.300000000000001. */
const sum = (values: number[]) => Math.round(values.reduce((n, v) => n + v * 100, 0)) / 100;

export function checkSquad(players: readonly EnteredPlayer[]): SquadCheck {
  const problems: string[] = [];
  if (players.length !== SQUAD_SIZE) {
    problems.push(`A squad is ${SQUAD_SIZE} players; this has ${players.length}.`);
  }

  const names = players.map((p) => p.fullName.trim().replace(/\s+/g, ' '));
  if (names.some((n) => n.split(' ').length < 2)) {
    problems.push('Give every player a full name: first name and surname.');
  }
  const seen = new Set<string>();
  const twice = names.filter((n) => {
    const k = n.toLowerCase();
    if (seen.has(k)) return true;
    seen.add(k);
    return false;
  });
  if (twice.length) problems.push(`${twice[0]} is in the squad twice.`);

  const bad = (v: number) => !Number.isFinite(v) || v < 0 || v > 7;
  if (players.some((p) => p.playtomic !== null && bad(p.playtomic))) {
    problems.push('Every Playtomic rating must be between 0 and 7.');
  }
  if (players.some((p) => bad(p.rating))) problems.push('Every actual rating must be between 0 and 7.');

  const total = sum(players.map((p) => (Number.isFinite(p.rating) ? p.rating : 0)));
  const stars = squadStars(players.map((p) => p.rating));
  return { ok: problems.length === 0, total, stars, problems, over: stars > SQUAD_LIMIT };
}

/** A squad's stars; a rating not yet typed counts nothing rather than 1.5. */
export const squadStars = (ratings: readonly number[]) =>
  ratings.reduce((n, r) => n + (Number.isFinite(r) ? starsFor(r) : 0), 0);

/** "Monday 5 January 2027", in league time. */
export const closesOn = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-GB', {
        timeZone: 'Africa/Johannesburg',
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';
